package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.util.RelativeTime
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.dom.Img
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.css.px

@Composable
fun StandupCard(
    standup: StandupDto,
    onClick: () -> Unit,
) {
    val now = Clock.System.now()
    val tz = runCatching { TimeZone.of(standup.timezone) }.getOrDefault(TimeZone.UTC)
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .pressable()
            .gap(Spacing.md)
            .styleModifier {
                property("padding", "22px 24px")
                property("display", "flex")
                property("flex-direction", "column")
            }
            .onClick { onClick() },
        interactive = true,
    ) {
        HeaderRow(standup)
        ChipRow(standup)
        Divider()
        ScheduleRow(standup, tz)
        Divider()
        FooterRow(standup, now, tz)
    }
}

@Composable
private fun HeaderRow(standup: StandupDto) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = standup.name,
            modifier = Modifier
                .fontSize(20.px)
                .fontWeight(600)
                .color(Palette.textPrimary)
                .styleModifier {
                    property("letter-spacing", "-0.01em")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    property("min-width", "0")
                },
        )
        Badge(
            label = if (standup.enabled) "Active" else "Paused",
            tone = if (standup.enabled) BadgeTone.Success else BadgeTone.Neutral,
        )
    }
}

@Composable
private fun ChipRow(standup: StandupDto) {
    Row(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px"); property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SlackChannelChip(
            text = standup.slackChannelName ?: standup.slackChannelId,
            monoText = standup.slackChannelName == null,
        )
        InfoChip(
            icon = "◎",
            text = "${standup.memberCount} ${if (standup.memberCount == 1) "member" else "members"}",
        )
    }
}

@Composable
private fun SlackChannelChip(text: String, monoText: Boolean) {
    Row(
        modifier = Modifier.styleModifier {
            property("gap", "6px")
            property("align-items", "center")
            property("background-color", Palette.surfaceSubtle.cssRgb())
            property("border-radius", "999px")
            property("padding", "4px 10px")
            property("min-width", "0")
        },
    ) {
        Img(
            src = "/brand/slack.svg",
            attrs = Modifier
                .styleModifier {
                    property("width", "14px")
                    property("height", "14px")
                    property("flex-shrink", "0")
                }
                .toAttrs { alt("Slack") },
        )
        SpanText(
            text = "#",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary),
        )
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textSecondary)
                .then(if (monoText) Modifier.fontFamily(*Typography.MONO_STACK) else Modifier)
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
    }
}

@Composable
private fun ScheduleRow(
    standup: StandupDto,
    tz: TimeZone,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.sm)
            .styleModifier { property("align-items", "center") },
    ) {
        ScheduleGlyph()
        Column(
            modifier = Modifier.styleModifier {
                property("gap", "2px")
                property("min-width", "0")
            },
        ) {
            SpanText(
                text = humanizeCron(standup.scheduleCron),
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(500)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = shortTimezone(tz, standup.timezone),
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
        }
    }
}

@Composable
private fun FooterRow(
    standup: StandupDto,
    now: Instant,
    tz: TimeZone,
) {
    Row(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "20px") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        MetaColumn(
            label = "Last run",
            value = standup.lastStandupAt?.let { RelativeTime.format(it, now) } ?: "No runs yet",
            dim = standup.lastStandupAt == null,
        )
        MetaColumn(
            label = "Next run",
            value = when {
                !standup.enabled -> "Paused"
                standup.nextRunAt == null -> "Not scheduled"
                else -> nextRunDisplay(standup.nextRunAt!!, now, tz)
            },
            dim = !standup.enabled || standup.nextRunAt == null,
        )
    }
}

@Composable
private fun InfoChip(
    icon: String,
    text: String,
    monoText: Boolean = false,
) {
    Row(
        modifier = Modifier.styleModifier {
            property("gap", "6px")
            property("align-items", "center")
            property("background-color", Palette.surfaceSubtle.cssRgb())
            property("border-radius", "999px")
            property("padding", "4px 10px")
            property("min-width", "0")
        },
    ) {
        SpanText(
            text = icon,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary),
        )
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textSecondary)
                .then(if (monoText) Modifier.fontFamily(*Typography.MONO_STACK) else Modifier)
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
    }
}

@Composable
private fun Divider() {
    Box(
        modifier = Modifier.fillMaxWidth().styleModifier {
            property("height", "1px")
            property("background-color", Palette.borderSubtle.cssRgb())
        },
    ) {}
}

@Composable
private fun ScheduleGlyph() {
    Box(
        modifier = Modifier
            .width(36.px)
            .styleModifier {
                property("height", "36px")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("border-radius", "10px")
                property("background-color", Palette.accentSubtle.cssRgb())
                property("color", Palette.accent.cssRgb())
                property("flex-shrink", "0")
                property("font-size", "16px")
                property("font-weight", "600")
            },
    ) {
        LucideClock(size = 14, strokeColor = Palette.accent.cssRgb())
    }
}

@Composable
private fun MetaColumn(label: String, value: String, dim: Boolean) {
    Column(
        modifier = Modifier
            .gap(Spacing.xxs)
            .styleModifier {
                property("flex", "1 1 0")
                property("min-width", "0")
            },
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary)
                .styleModifier {
                    property("text-transform", "none")
                    property("letter-spacing", "0.06em")
                },
        )
        SpanText(
            text = value,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(if (dim) Palette.textTertiary else Palette.textPrimary)
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
    }
}

